/**
 * BlogProgressBar — Thin reading-progress bar fixed to the top of the page.
 *
 * - Fixed at z-50, 3px tall, full viewport width
 * - Fills with the category color as the reader scrolls through the article
 * - Appears only once the reader has scrolled past the article header
 * - Progress is relative to the article element (enters viewport → bottom exits)
 * - Smooth CSS transition on width changes
 */

import { useEffect, useRef, useState } from 'react';
import { CATEGORY_CONFIG } from '../../types/blog';
import type { BlogCategory } from '../../types/blog';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BlogProgressBarProps {
  /** Blog category — determines the bar color. Defaults to brand teal. */
  category?: BlogCategory;
  /** Ref to the <article> element to measure scroll progress against. */
  articleRef?: React.RefObject<HTMLElement | null>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BlogProgressBar({ category, articleRef }: BlogProgressBarProps) {
  const [progress, setProgress] = useState(0);   // 0–100
  const [active, setActive] = useState(false);    // whether to show the bar

  // Resolved color: use category color or fall back to brand teal
  const barColor = category ? CATEGORY_CONFIG[category].color : '#00C2A8';

  // Stable ref for the scroll handler so we can remove the exact listener
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    function getArticle(): HTMLElement | null {
      return (articleRef?.current as HTMLElement | null)
        ?? document.querySelector('article');
    }

    function updateProgress() {
      const article = getArticle();
      if (!article) {
        setProgress(0);
        setActive(false);
        return;
      }

      const rect = article.getBoundingClientRect();
      const windowH = window.innerHeight;

      // The article hasn't entered view yet
      if (rect.top > windowH) {
        setProgress(0);
        setActive(false);
        return;
      }

      // Total scrollable distance: from when article top hits viewport top
      // to when article bottom exits viewport bottom
      const totalScrollable = rect.height - windowH;

      if (totalScrollable <= 0) {
        // Article is shorter than the viewport — fully visible
        setProgress(100);
        setActive(rect.top < 0 || rect.bottom > windowH);
        return;
      }

      // How far we've scrolled into the article
      // rect.top starts at 0 (article top at viewport top) and goes negative as we scroll
      const scrolled = -rect.top;

      if (scrolled <= 0) {
        // We're above or just at the article top — not yet started
        setProgress(0);
        setActive(false);
        return;
      }

      const pct = Math.min(100, Math.max(0, (scrolled / totalScrollable) * 100));

      setProgress(pct);
      // Show bar only once we've scrolled at least a tiny bit into the article
      setActive(scrolled > 40);
    }

    function onScroll() {
      if (rafRef.current !== undefined) return; // already queued
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = undefined;
        updateProgress();
      });
    }

    // Run once on mount to handle pre-scrolled pages
    updateProgress();

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current !== undefined) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [articleRef]);

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Lesefremdrift"
      className="fixed top-0 left-0 right-0 z-50 h-[3px] pointer-events-none"
    >
      <div
        className="h-full origin-left"
        style={{
          width: `${progress}%`,
          backgroundColor: barColor,
          opacity: active ? 1 : 0,
          transition: 'width 80ms linear, opacity 300ms ease',
          willChange: 'width',
        }}
      />
    </div>
  );
}
